import React, { useState, useEffect } from 'react';
import './Footer.css';
import { supabase } from '../lib/supabase';

interface AppSettings {
  [key: string]: string;
}

interface FooterProps {
  onTermsClick?: () => void;
  onPrivacyClick?: () => void;
}

const Footer: React.FC<FooterProps> = ({ onTermsClick, onPrivacyClick }) => {
  const [settings, setSettings] = useState<AppSettings>({});
  const [loading, setLoading] = useState(true);
  const [isFlutterApp, setIsFlutterApp] = useState(false);

  // Flutter 앱 환경 감지
  useEffect(() => {
    const checkFlutterApp = () => {
      const platform = localStorage.getItem('flutter_device_platform');
      if (platform === 'ios' || platform === 'android') {
        setIsFlutterApp(true);
      }
    };

    // 초기 확인
    checkFlutterApp();

    // localStorage 변경 감지 (Flutter에서 설정할 때)
    const interval = setInterval(checkFlutterApp, 500);
    setTimeout(() => clearInterval(interval), 3000); // 3초 후 중지

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('setting_key, setting_value')
          .eq('is_active', true);

        if (error) {
          console.error('Error fetching settings:', error);
          return;
        }

        const settingsMap: AppSettings = {};
        data?.forEach((item: { setting_key: string; setting_value: string }) => {
          settingsMap[item.setting_key] = item.setting_value;
        });

        setSettings(settingsMap);
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Flutter 앱에서는 푸터 숨김
  if (isFlutterApp) {
    return null;
  }

  if (loading) {
    return null;
  }

  return (
    <footer className="web-footer">
      <div className="footer-content">
        <div className="footer-links">
          <button className="footer-link" onClick={onTermsClick}>이용약관</button>
          <button className="footer-link" onClick={onPrivacyClick}>개인정보처리방침</button>
        </div>

        <div className="footer-main">
          <div className="footer-left">
            <div className="footer-info">
              <div className="footer-info-row">
                <span>{settings.company_name || '회사명'}</span>
                <span className="footer-divider">|</span>
                <span>대표: {settings.ceo_name || '-'}</span>
                <span className="footer-divider">|</span>
                <span>사업자번호: {settings.business_number || '-'}</span>
              </div>

              {settings.telecom_sales_number && (
                <div className="footer-info-row">
                  <span>통신판매업: {settings.telecom_sales_number}</span>
                </div>
              )}

              <div className="footer-info-row">
                <span>주소: {settings.address || '-'}</span>
              </div>

              <div className="footer-info-row">
                <span>고객센터: {settings.customer_center || '-'}</span>
                <span className="footer-divider">|</span>
                <span>E-Mail: {settings.contact_email || '-'}</span>
              </div>
            </div>
          </div>

          <div className="footer-right">
            <a
              href="https://ictmarket.or.kr:8443/precon/pop_CertIcon.do?PRECON_REQ_ID=PRE0000160340"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-cert-link"
            >
              <img
                src="https://ictmarket.or.kr:8443/getCertIcon.do?cert_icon=KL22020459852Y001"
                alt="사전승낙서 인증마크"
                className="footer-cert-img footer-cert-img-large"
              />
            </a>
            <a
              href="https://ictmarket.or.kr:8443/precon/pop_CertIcon.do?PRECON_REQ_ID=PRE0000190897"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-cert-link"
            >
              <img
                src="https://ictmarket.or.kr:8443/getCertIcon.do?cert_icon=KP25012206905L001"
                alt="정보통신진흥협회 인증마크"
                className="footer-cert-img footer-cert-img-small"
              />
            </a>
          </div>
        </div>

        <div className="footer-copyright">
          Copyright © {settings.company_name || '삐삐'}, All Rights Reserved.
        </div>
      </div>
    </footer>
  );
};

export default Footer;
